import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { message } from 'antd';
import { DataCollBlockedStatus } from '../components/dataCollUpload/types';
import { apiUrl } from '../common/url';

interface UseDataCollBlockedStatusProps {
  dataCollName: string | undefined;
  client: { id: string; token: string };
  setDataCollBlockedStatus: React.Dispatch<React.SetStateAction<DataCollBlockedStatus | undefined>>;
  adminRoles?: string[];
  adminToken?: string;
  currentBlockedStatus?: DataCollBlockedStatus;
}

export const useDataCollBlockedStatus = ({
  dataCollName,
  client,
  setDataCollBlockedStatus,
  adminRoles,
  adminToken,
  currentBlockedStatus,
}: UseDataCollBlockedStatusProps) => {
  const { instance } = useMsal();

  const updateBlockedStatus = (isBlocked: boolean, allowDataEditing?: boolean) => {
    if (!dataCollName) return Promise.reject('No table name provided');

    // Determine which API to use based on current status
    let url: string | null = null;
    let token: string | null = null;

    if (!currentBlockedStatus || currentBlockedStatus.blocking_user_is_current_user) {
      // Use client API if no blocking status or if current user is blocking
      url = `${apiUrl}/b2zero/${client.id}/update_data_coll_blocked_status`;
      token = client.token;
    } else if (adminRoles?.includes('data_admin') && adminToken) {
      // Use admin API if table is blocked by another user and current user is admin
      url = `${apiUrl}/admin/update_data_coll_blocked_status/${client.id}`;
      token = adminToken;
    }

    if (!token || !url) {
      return Promise.reject(
        'Die Tabelle wird bereits von einem anderen Benutzer blockiert. Bitte wenden Sie sich an einen Administrator oder warten Sie, bis die Tabelle wieder freigegeben wurde.'
      );
    }

    return axios
      .post(
        url,
        {
          data_coll_name: dataCollName,
          isBlocked,
          allow_data_editing: allowDataEditing,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => {
        const dataCollBlockedStatusNew = response.data as DataCollBlockedStatus;
        dataCollBlockedStatusNew.blocking_user_is_current_user =
          dataCollBlockedStatusNew.blocking_user_id === instance.getActiveAccount()?.localAccountId;
        setDataCollBlockedStatus(dataCollBlockedStatusNew);
        return dataCollBlockedStatusNew;
      })
      .catch((error: any) => {
        if (error.response?.status === 409) {
          message.error('Die Tabelle wird bereits von einem anderen Benutzer bearbeitet');
        } else if (error.response?.status === 403) {
          message.error('Die Tabelle ist gesperrt');
        } else {
          message.error(
            `Fehler beim ${isBlocked ? 'Sperren' : 'Entsperren'} der Tabelle: ${error.message}`
          );
        }
        throw error;
      });
  };

  return { updateBlockedStatus };
};
